import {
    HttpClient,
    HttpHeaders
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    Actions,
    createEffect, ofType
} from '@ngrx/effects';
import {
    Action,
    Store
} from '@ngrx/store';
import { Observable } from 'rxjs';
import {
    catchError,
    map, mergeMap, skipWhile,
    switchMap
} from 'rxjs/operators';

import { environment } from '../../../../environments/environment';
import { SET_LOGGED_IN, SET_USER_PROFILE, SetLoggedIn, SetUserProfile } from '../../../store/auth/auth.actions';

import {
    HeaderKey,
    HeadersValues
} from '../../constants/constants';
import { Favourite } from '../../models';

import {
    Delete,
    FavouritesActionTypes,
    Load,
    FavouritesActionFail,
    LoadSuccess,
    SetFavourite,
    UnsetFavourite,
    Upsert, Clear
} from './items.actions';
import { HTTP_STATUS_404 } from '../../../constants/application-constants';
import { NzNotificationService } from 'ng-zorro-antd/notification';

interface DeleteType {
    id: number;
    catalogueItemId: number;
}

@Injectable()
export class FavouritesEffects {
    favouriteUrl = `${environment.favouritesHost}${environment.favouriteBaseURL}`;


    load$: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType(FavouritesActionTypes.LOAD),
        switchMap((action: Load) => {
            const headers = new HttpHeaders()
                .set(HeaderKey.ACCEPT,
                    [HeadersValues.ACCEPT_FAVOURITE, HeadersValues.ACCEPT_ERROR].join(','))
                .set('timeout', '10000');

            return this.httpClient.get<Favourite[]>(environment.favouritesHost + '/favourite',
                {
                    headers
                });
        }),
        map((loadedFavourites: Favourite[]) => new LoadSuccess(loadedFavourites)),
        catchError(err => {

            const msg = err.status === HTTP_STATUS_404 ? err.error : 'Something went wrong.';

            this.notification.error('Failed to load your favourites', msg);
            return [
                new FavouritesActionFail()
            ];
        })
    ));


    setFavourite$: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType(FavouritesActionTypes.SET_FAVOURITE),
        mergeMap((action: SetFavourite) => {
            const requestUrl = `${this.favouriteUrl}/${action.catalogueItemId}/favourite`;
            const headers = new HttpHeaders()
                .set(HeaderKey.ACCEPT,
                    [HeadersValues.ACCEPT_FAVOURITE, HeadersValues.ACCEPT_ERROR].join(','));

            return this.httpClient.post<Favourite>(requestUrl, null,
                {
                    headers: headers
                }).pipe(
                catchError(_ => {

                    this.notification.error('Failed to set favourites', 'Something went wrong. Please try again`');

                    return [
                        new FavouritesActionFail(null, action.catalogueItemId)
                    ];
                }));

        }),
        map((response: Favourite | FavouritesActionFail) => {
            if (response instanceof FavouritesActionFail) {
                return response;
            }
            return new Upsert(response);
        })
    ));


    unsetFavourite$: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType(FavouritesActionTypes.UNSET_FAVOURITE),
        mergeMap((action: UnsetFavourite) => {
            const requestUrl = `${this.favouriteUrl}/${action.item.catalogueItemId}/favourite`;
            const headers = new HttpHeaders()
                .set(HeaderKey.ACCEPT,
                    [HeadersValues.ACCEPT_FAVOURITE, HeadersValues.ACCEPT_ERROR].join(','));

            return this.httpClient.delete<Favourite>(requestUrl,
                {
                    headers: headers
                }).pipe(
                map(_ => ({ id: action.item.id, catalogueItemId: action.item.catalogueItemId })),
                catchError(error => {

                    this.notification.error('Failed to unset favourite', 'Something went wrong. Please try again');

                    return [
                        new FavouritesActionFail(null, action.item.catalogueItemId)
                    ];
                }));

        }),
        map((response: DeleteType | FavouritesActionFail) => {
            if (response instanceof FavouritesActionFail) {
                return response;
            }
            return new Delete(response.id, response.catalogueItemId);
        })
    ));


    onSetProfile$: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType(SET_USER_PROFILE),
        map((action: SetUserProfile) => {
            if (action.profile.name) {
                return new Load();
            } else {
                return new Clear();
            }
        })
    ));


    onSetLoginUser$: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType(SET_LOGGED_IN),
        skipWhile((action: SetLoggedIn) => action.loggedIn),
        map((action: SetLoggedIn) => new Clear())
    ));

    constructor(private actions$: Actions,
        private httpClient: HttpClient,
        private notification: NzNotificationService) {
    }
}

